import React, { useState } from "react";
import { X } from "lucide-react";

interface Asset {
  id: string;
  name: string;
  url: string;
  alt_description?: string;
  metadata?: any;
}

interface MetadataModalProps {
  asset: Asset;
  onClose: () => void;
  onSave: (asset: Asset, metadata: any) => void;
}

const LICENSE_OPTIONS = [
  { value: "", label: "Select a license" },
  { value: "CC0", label: "CC0 (Public Domain)" },
  { value: "CC-BY-4.0", label: "CC BY 4.0" },
  { value: "CC-BY-SA-4.0", label: "CC BY-SA 4.0" },
  { value: "CC-BY-NC-4.0", label: "CC BY-NC 4.0" },
  { value: "CC-BY-ND-4.0", label: "CC BY-ND 4.0" },
  { value: "CC-BY-NC-SA-4.0", label: "CC BY-NC-SA 4.0" },
  { value: "CC-BY-NC-ND-4.0", label: "CC BY-NC-ND 4.0" },
  { value: "rights-reserved", label: "All Rights Reserved" },
  { value: "custom", label: "Custom License" },
];

export default function MetadataModal({
  asset,
  onClose,
  onSave,
}: MetadataModalProps) {
  const [metadata, setMetadata] = useState({
    alt_description: asset.alt_description || "",
    caption: asset.metadata?.caption || "",
    credit: asset.metadata?.credit || "",
    license: asset.metadata?.license || "",
    customLicense: "",
    tags: (asset.metadata?.tags || []).join(", "),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const updatedMetadata = {
      ...asset.metadata,
      caption: metadata.caption,
      credit: metadata.credit,
      license:
        metadata.license === "custom"
          ? metadata.customLicense
          : metadata.license,
      tags: metadata.tags
        .split(",")
        .map((tag) => tag.trim())
        .filter(Boolean),
    };

    onSave(asset, {
      ...metadata,
      ...updatedMetadata,
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl">
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Edit Image Metadata
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Image Preview */}
          <div>
            <div className="aspect-video rounded-lg overflow-hidden bg-gray-100 mb-4">
              <img
                src={asset.url}
                alt={metadata.alt_description || asset.name}
                className="w-full h-full object-contain"
              />
            </div>
            <div className="text-sm text-gray-500 space-y-2">
              <p>
                <strong>File name:</strong>{" "}
                <span className="break-all">{asset.name}</span>
              </p>
              <div className="flex">
                <strong className="flex-shrink-0">URL:</strong>
                <div className="ml-1 break-all">{asset.url}</div>
              </div>
            </div>
          </div>

          {/* Metadata Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="alt_description"
                className="block text-sm font-medium text-gray-700"
              >
                Alt Description
              </label>
              <input
                type="text"
                id="alt_description"
                value={metadata.alt_description}
                onChange={(e) =>
                  setMetadata({ ...metadata, alt_description: e.target.value })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring focus:ring-[#F98B3D] focus:ring-opacity-50"
                placeholder="Describe the image for accessibility"
              />
            </div>

            <div>
              <label
                htmlFor="caption"
                className="block text-sm font-medium text-gray-700"
              >
                Caption
              </label>
              <input
                type="text"
                id="caption"
                value={metadata.caption}
                onChange={(e) =>
                  setMetadata({ ...metadata, caption: e.target.value })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring focus:ring-[#F98B3D] focus:ring-opacity-50"
                placeholder="Add a caption for the image"
              />
            </div>

            <div>
              <label
                htmlFor="credit"
                className="block text-sm font-medium text-gray-700"
              >
                Credit
              </label>
              <input
                type="text"
                id="credit"
                value={metadata.credit}
                onChange={(e) =>
                  setMetadata({ ...metadata, credit: e.target.value })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring focus:ring-[#F98B3D] focus:ring-opacity-50"
                placeholder="Credit the image source or creator"
              />
            </div>

            <div>
              <label
                htmlFor="license"
                className="block text-sm font-medium text-gray-700"
              >
                License
              </label>
              <select
                id="license"
                value={metadata.license}
                onChange={(e) =>
                  setMetadata({ ...metadata, license: e.target.value })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring focus:ring-[#F98B3D] focus:ring-opacity-50"
              >
                {LICENSE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {metadata.license === "custom" && (
                <input
                  type="text"
                  value={metadata.customLicense}
                  onChange={(e) =>
                    setMetadata({ ...metadata, customLicense: e.target.value })
                  }
                  className="mt-2 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring focus:ring-[#F98B3D] focus:ring-opacity-50"
                  placeholder="Enter custom license details"
                />
              )}
            </div>

            <div>
              <label
                htmlFor="tags"
                className="block text-sm font-medium text-gray-700"
              >
                Tags
              </label>
              <input
                type="text"
                id="tags"
                value={metadata.tags}
                onChange={(e) =>
                  setMetadata({ ...metadata, tags: e.target.value })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring focus:ring-[#F98B3D] focus:ring-opacity-50"
                placeholder="Add tags separated by commas"
              />
              <p className="mt-1 text-sm text-gray-500">
                Separate tags with commas
              </p>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F98B3D]"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#F98B3D] hover:bg-[#e07a2c] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F98B3D]"
              >
                Save Changes
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
